import React from 'react';
import {
  Box,
  Typography,
  Divider,
  Stack,
} from '@mui/material';
import { alpha, darken, lighten } from '@mui/material/styles';

const DEFAULT_PARAGRAPHS = [
  'We begin each external assessment by defining the authorized scope and building an accurate picture of the internet-facing attack surface. The assessment team reviews the provided targets, correlates them with passive intelligence, and performs reconnaissance to identify exposed domains, subdomains, hosts, and supporting infrastructure that may expand the reachable perimeter.',
  'After the initial mapping phase, we validate exposure through controlled active enumeration. We perform subdomain discovery, DNS resolution, port scanning, and service identification to determine which assets are reachable and which entry points warrant deeper review. From there, we fingerprint the observable technology stack, including web servers, frameworks, middleware, TLS configurations, and other implementation details that shape the likely attack paths.',
  'Once the environment is understood, we align the discovered technologies and services with relevant weaknesses, misconfigurations, and known CVEs. The assessment team uses that context to guide targeted exploitation, prioritizing the issues most likely to produce real impact. Testing is performed deliberately and with evidence-driven judgment so that findings reflect validated risk rather than theoretical exposure.',
  'Throughout the engagement, we document affected assets, record supporting evidence, and capture the technical details required to reproduce each issue. The assessment team then translates those results into a clear report that explains what was identified, how it was validated, and which remediation actions should be prioritized first. The goal is to provide both leadership and technical teams with a practical path from reconnaissance through remediation.',
];

const MethodologySection = ({
  primary = '#0A68FF',
  id = 'methodology',
  title = 'Methodology',
  intro = 'This section summarizes how we normally conduct an external penetration test from reconnaissance and enumeration through validation and reporting.',
  summary = 'We use a staged methodology so each phase builds on verified observations from the phase before it, allowing the assessment team to move from broad attack-surface visibility into focused validation and defensible reporting.',
  paragraphs = DEFAULT_PARAGRAPHS,
}) => {
  const pDark = darken(primary, 0.35);
  const pMid = darken(primary, 0.15);
  const pLite = lighten(primary, 0.28);
  const ink = '#0f172a';

  return (
    <Box
      id={id}
      sx={{
        maxWidth: '8.5in',
        p: '0.72in 0.82in',
        bgcolor: '#fff',
        color: ink,
        breakInside: 'avoid',
        pageBreakInside: 'avoid',
      }}
    >
      <Stack spacing={1.1} sx={{ mb: 1.4 }}>
        <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: 0.2 }}>
          {title}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: alpha(ink, 0.8),
            lineHeight: 1.6,
            maxWidth: '92%',
          }}
        >
          {intro}
        </Typography>
      </Stack>

      <Divider
        sx={{
          mb: 2.1,
          borderColor: alpha(ink, 0.08),
          '&::after': {
            content: '""',
            display: 'block',
            height: 4,
            width: 180,
            mt: 1,
            borderRadius: 2,
            background: `linear-gradient(90deg, ${pDark}, ${pMid} 60%, ${pLite})`,
          },
          '@media print': { '&::after': { background: pMid } },
        }}
      />

      <Box
        sx={{
          mb: 1.8,
          px: 2,
          py: 1.5,
          borderRadius: 2,
          bgcolor: alpha(primary, 0.045),
          border: `1px solid ${alpha(primary, 0.14)}`,
        }}
      >
        <Typography variant="body1" sx={{ color: alpha(ink, 0.86), lineHeight: 1.6 }}>
          {summary}
        </Typography>
      </Box>

      <Stack spacing={1.45}>
        {paragraphs.map((paragraph) => (
          <Typography
            key={paragraph}
            variant="body1"
            sx={{
              color: alpha(ink, 0.96),
              lineHeight: 1.65,
            }}
          >
            {paragraph}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
};

export default MethodologySection;
